import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { catchError } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { HttpHeaders } from '@angular/common/http';
import { HttpLink } from 'apollo-angular/http';
import { InMemoryCache } from '@apollo/client/core';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class ShopifyService {

  private storefrontToken = '';
  private shopifyUrl = '';
  private httpLink!: HttpLink;

  constructor(private apollo: Apollo, httpLink: HttpLink, private http: HttpClient) {
    this.httpLink = httpLink;
  }

  setShopifyCredentials(isMain: boolean) {
    if (isMain) {
      this.storefrontToken = 'e5e0a74a61949d5eb07d7accb2572428';
      this.shopifyUrl = 'https://xqjcbd-u0.myshopify.com/api/2025-01/graphql.json';
    } else {
      this.storefrontToken = '542e24546f02a4debeed17e574aeb7ab';
      this.shopifyUrl = 'https://f71jh4-21.myshopify.com/api/2025-01/graphql.json';
    }
    this.createApolloClient();
  }

  private clientCreated = false;

  private createApolloClient() {
    if (!this.shopifyUrl || !this.storefrontToken) {
      throw new Error('Shopify credentials are not set!');
    }
    if (this.clientCreated) {
      this.apollo.removeClient();
    }
    this.clientCreated = true;
    const lang = sessionStorage.getItem('lang') || 'en';
    this.apollo.create({
      cache: new InMemoryCache(),
      link: this.httpLink.create({
        uri: this.shopifyUrl,
        headers: new HttpHeaders({
          'X-Shopify-Storefront-Access-Token': this.storefrontToken,
          'Content-Type': 'application/json',
          'Accept-Language': lang
        })
      })
    });
  }

  // 1. Create a Cart with Market Context
  createCart(countryCode: string) {
    const CREATE_CART = gql`
      mutation createCartWithContext($country: CountryCode) @inContext(country: $country) {
        cartCreate(input: {}) {
          cart {
            id
            checkoutUrl
          }
          userErrors {
            message
          }
        }
      }
    `;

    return this.apollo.mutate({
      mutation: CREATE_CART,
      variables: {
        country: countryCode
      }
    }).pipe(
      catchError(error => {
        console.error('Error creating cart:', error);
        return of(null);
      })
    );
  }

  // 2. Add a Product to the Cart
  addProductToCart(cartId: string, variantId: string, quantity: number = 1) {
    return this.addLinesToCart(cartId, [{ variantId, quantity }]);
  }

  // Add multiple line items to the cart (e.g. Valentine's bundle + single)
  addLinesToCart(cartId: string, lines: Array<{ variantId: string; quantity: number }>) {
    const ADD_LINE_ITEMS = gql`
      mutation($cartId: ID!, $lines: [CartLineInput!]!) {
        cartLinesAdd(cartId: $cartId, lines: $lines) {
          cart {
            id
            checkoutUrl
          }
          userErrors {
            message
          }
        }
      }
    `;

    const cartLines = lines.map(({ variantId, quantity }) => ({
      merchandiseId: variantId,
      quantity
    }));

    return this.apollo.mutate({
      mutation: ADD_LINE_ITEMS,
      variables: {
        cartId,
        lines: cartLines
      }
    }).pipe(
      catchError(error => {
        console.error('Error adding lines to cart:', error);
        return of(null);
      })
    );
  }

  // 3. Update Cart Attributes (for attaching PDF URL)
  updateCartAttributes(cartId: string, pdfUrl: string, magnetUrl: string, referralCode: string | null) {
    const UPDATE_CART_ATTRIBUTES = gql`
      mutation($cartId: ID!, $attributes: [AttributeInput!]!) {
        cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
          cart {
            id
            attributes {
              key
              value
            }
          }
          userErrors {
            message
          }
        }
      }
    `;

    return this.apollo.mutate({
      mutation: UPDATE_CART_ATTRIBUTES,
      variables: {
        cartId,
        attributes: [
          { key: 'pdfUrl', value: pdfUrl },
          { key: 'magnetUrl', value: magnetUrl },
          { key: 'referral', value: referralCode }
        ]
      }
    }).pipe(
      catchError(error => {
        console.error('Error updating cart attributes:', error);
        return of(null);
      })
    );
  }

  // 4. Update Cart Attributes for Multiple Designs
  updateCartAttributesMultiple(
    cartId: string, 
    designs: any[], 
    referralCode: string | null
  ) {
    const UPDATE_CART_ATTRIBUTES = gql`
      mutation($cartId: ID!, $attributes: [AttributeInput!]!) {
        cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
          cart {
            id
            attributes {
              key
              value
            }
          }
          userErrors {
            message
          }
        }
      }
    `;

    // Create attributes array
    const attributes: any[] = [
      { key: 'referral', value: referralCode || '' }
    ];
    
    // Add each design's data
    designs.forEach((design, index) => {
      attributes.push(
        { key: `design_${index + 1}_pdfUrl`, value: design.pdfUrl },
        { key: `design_${index + 1}_magnetUrls`, value: design.magnetUrls },
        { key: `design_${index + 1}_quantity`, value: design.quantity.toString() }
      );
    });
    
    // Add total designs count
    attributes.push({ key: 'totalDesigns', value: designs.length.toString() });

    return this.apollo.mutate({
      mutation: UPDATE_CART_ATTRIBUTES,
      variables: {
        cartId,
        attributes
      }
    }).pipe(
      catchError(error => {
        console.error('Error updating cart attributes:', error);
        return of(null);
      })
    );
  }

  getProductInfo(productHandle: string, countryCode: string): Observable<any> {
    const lang = sessionStorage.getItem('lang') || 'en';
    const query = `
        query getProduct($handle: String!, $country: CountryCode) @inContext(country: $country) {
          productByHandle(handle: $handle) {
            id
            title
            description
            images(first: 1) {
              edges {
                node {
                  url
                }
              }
            }
            variants(first: 1) {
              edges {
                node {
                  price {
                    amount
                    currencyCode
                  }
                  compareAtPrice {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
      `;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': this.storefrontToken,
      'Accept-Language': lang
    });
    const variables = {
      handle: productHandle,
      country: countryCode
    };
    return this.http.post<any>(this.shopifyUrl, { query, variables }, { headers });
  }

  getProductByHandle(handle: string, countryCode?: string): Observable<any> {
    const lang = sessionStorage.getItem('lang') || 'en';
    const country = countryCode || sessionStorage.getItem('country') || 'MK';

    const query = `
      query getProduct($handle: String!, $country: CountryCode) @inContext(country: $country) {
        productByHandle(handle: $handle) {
          id
          title
          description
          handle
          availableForSale
          images(first: 1) {
            edges {
              node {
                url
              }
            }
          }
          variants(first: 1) {
            edges {
              node {
                id
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
                availableForSale
              }
            }
          }
        }
      }
    `;

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': this.storefrontToken,
      'Accept-Language': lang
    });

    const variables = {
      handle,
      country
    };

    return this.http.post<any>(this.shopifyUrl, { query, variables }, { headers });
  }

  // Get all products with available_for_sale filter
  getProducts(first: number = 250, countryCode?: string, after?: string): Observable<any> {
    const lang = sessionStorage.getItem('lang') || 'en';
    const country = countryCode || sessionStorage.getItem('country') || 'MK';
    
    const query = `
      query getProducts($first: Int!, $after: String, $country: CountryCode, $query: String) @inContext(country: $country) {
        products(first: $first, after: $after, query: $query) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              title
              description
              handle
              availableForSale
              metafield(namespace: "custom", key: "show_initial") {
                value
              }
              images(first: 1) {
                edges {
                  node {
                    url
                  }
                }
              }
              variants(first: 1) {
                edges {
                  node {
                    id
                    price {
                      amount
                      currencyCode
                    }
                    compareAtPrice {
                      amount
                      currencyCode
                    }
                    availableForSale
                  }
                }
              }
            }
          }
        }
      }
    `;

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': this.storefrontToken,
      'Accept-Language': lang
    });

    const variables = {
      first,
      after: after || null,
      country,
      query: 'available_for_sale:true'
    };

    return this.http.post<any>(this.shopifyUrl, { query, variables }, { headers });
  }
}